import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Upload, FileText, FileWarning, Trash2, Plus, Save, Eye } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const BRANDS = [["unitile", "Unitile"], ["univicoustic", "UniVicoustic"], ["uflex", "U-Flex"]];

function Row({ c, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const CHUNK = 4 * 1024 * 1024;
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 60 * 1024 * 1024) { toast.error("PDF is larger than 60 MB — compress it first"); return; }
    setBusy(true); setPct(0);
    try {
      const total = Math.ceil(f.size / CHUNK);
      const uploadId = `${c.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("total", String(total));
        fd.append("chunk", f.slice(i * CHUNK, (i + 1) * CHUNK), `${i}.part`);
        await api.post(`/lead-desk/catalogues/${c.id}/file/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        setPct(Math.round(((i + 1) / (total + 1)) * 100));
      }
      await api.post(`/lead-desk/catalogues/${c.id}/file/complete`, { upload_id: uploadId, file_name: f.name }, { timeout: 120000 });
      setPct(100);
      toast.success(`PDF uploaded for ${c.name} (${(f.size / 1048576).toFixed(1)} MB)`); onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const view = async () => {
    try { const r = await api.get(`/lead-desk/catalogues/${c.id}/file`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/lead-desk/catalogues/${c.id}`); toast.success("Deleted"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (v) => { try { await api.put(`/lead-desk/catalogues/${c.id}`, { is_active: v }); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className={`flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-0 ${c.is_active === false ? "opacity-50" : ""}`} data-testid={`cat-row-${c.id}`}>
      {c.has_file ? <FileText className="h-4 w-4 text-emerald-600 shrink-0" /> : <FileWarning className="h-4 w-4 text-amber-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
        <p className="text-xs text-slate-500 truncate">{c.group || "—"}{c.has_file ? ` · ${c.file_name} · ${(c.size / 1024 / 1024).toFixed(2)} MB` : " · no PDF yet"}</p>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid={`cat-file-${c.id}`} />
      {c.has_file && <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={view} data-testid={`cat-view-${c.id}`}><Eye className="h-3.5 w-3.5" /> View</Button>}
      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`cat-upload-${c.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {busy ? `${pct}%` : c.has_file ? "Replace" : "Upload PDF"}</Button>
      <Switch checked={c.is_active !== false} onCheckedChange={toggle} title="Active" data-testid={`cat-active-${c.id}`} />
      <button onClick={del} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`cat-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function AddRow({ brand, onAdded }) {
  const [name, setName] = useState(""); const [group, setGroup] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/lead-desk/catalogues", { brand, name, group }); setName(""); setGroup(""); onAdded(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50/70">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New product / catalogue name" className="h-8 text-xs flex-1 min-w-[180px]" data-testid={`cat-new-name-${brand}`} />
      <Input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Group (e.g. Fabric)" className="h-8 text-xs w-44" data-testid={`cat-new-group-${brand}`} />
      <Button size="sm" className="h-8 gap-1 text-xs bg-slate-900" onClick={add} data-testid={`cat-add-${brand}`}><Plus className="h-3.5 w-3.5" /> Add</Button>
    </div>
  );
}

function TemplateEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["ld-template"], queryFn: async () => (await api.get("/lead-desk/template")).data });
  const [f, setF] = useState(null);
  const cur = f || data || { subject: "", body: "" };
  const save = async () => {
    try { await api.put("/lead-desk/template", cur); toast.success("Template saved"); qc.invalidateQueries({ queryKey: ["ld-template"] }); setF(null); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-4 border-slate-200" data-testid="ld-template-editor">
      <h3 className="font-heading font-semibold text-slate-900">Catalogue email template</h3>
      <p className="text-xs text-slate-500 mb-3">Placeholders: {"{first_name} {name} {event} {brand} {BRAND} {catalogues}"} — the signature (name, designation, phone, email) is added automatically from the sender's account. A line in ALL CAPS becomes a section heading in the email.</p>
      <div className="space-y-2">
        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Subject</Label><Input value={cur.subject} onChange={(e) => setF({ ...cur, subject: e.target.value })} className="h-9" data-testid="ld-template-subject" /></div>
        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Body</Label><Textarea value={cur.body} onChange={(e) => setF({ ...cur, body: e.target.value })} rows={9} className="font-mono text-xs" data-testid="ld-template-body" /></div>
        <Button onClick={save} disabled={!f} className="gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="ld-template-save"><Save className="h-4 w-4" /> Save template</Button>
      </div>
    </Card>
  );
}

function WaTemplateEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["ld-wa-template"], queryFn: async () => (await api.get("/lead-desk/whatsapp-template")).data });
  const [f, setF] = useState(null);
  const cur = f || data || { name: "", language: "en", body: "", header: "document", button: false };
  const mode = cur.header === "document" ? "document" : cur.button ? "button" : "none";
  const setMode = (m) => setF({ ...cur, header: m === "document" ? "document" : "none", button: m === "button", body: data?.default_bodies?.[m] || cur.body, name: data?.default_names?.[m] || cur.name });
  const VARS = { none: ["First name", "Exhibition", "Catalogue link"], button: ["First name", "Exhibition"], document: ["Visitor name", "Brand", "Exhibition", "Catalogue name", "Rep name"] };
  const save = async () => {
    try { await api.put("/lead-desk/whatsapp-template", cur); toast.success("WhatsApp template saved"); qc.invalidateQueries({ queryKey: ["ld-wa-template"] }); setF(null); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-4 border-emerald-200" data-testid="ld-wa-template-editor">
      <h3 className="font-heading font-semibold text-slate-900">WhatsApp catalogue template (Interakt)</h3>
      <p className="text-xs text-slate-500 mb-1">Interakt connection: {data ? (data.configured ? <span className="text-emerald-700 font-medium">connected</span> : <span className="text-red-600 font-medium">API key missing on this server</span>) : "…"}</p>
      <p className="text-xs text-slate-500 mb-3">WhatsApp only delivers <b>pre-approved</b> templates to new contacts. Create the template in Interakt → Templates, wait for approval, then enter its name and language here. Variables, in order: {VARS[mode].map((v, i) => `{{${i + 1}}} ${v}`).join(" · ")}{mode === "none" ? " · optional {{4}} Rep name · {{5}} Designation · {{6}} Phone — only sent if your approved body uses them" : ""}.</p>
      {mode === "button" && <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5 mb-3" data-testid="ld-wa-button-hint">Template button: type <b>Visit website</b> → <b>Dynamic URL</b> → <code className="font-mono">{data?.button_url}</code>, label <b>View Catalogue</b>. The CRM fills <code>{"{{1}}"}</code> of the button with a short code per visitor.</p>}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="ld-wa-header-type">
        {[["button", "Text + View Catalogue button", "no URL in the text — the catalogue opens from a button under the message"], ["none", "Text with link", "the catalogue link is written in the message text"], ["document", "PDF attached", "one message per catalogue PDF; catalogues without a PDF are skipped"]].map(([h, label, hint]) => (
          <button key={h} type="button" onClick={() => setMode(h)} className={`text-left rounded-md border px-3 py-2 text-xs flex-1 min-w-[200px] ${mode === h ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`ld-wa-header-${h}`}>
            <p className="font-semibold text-slate-900">{label}</p><p className="text-slate-500">{hint}</p>
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-[1fr_120px] gap-2 mb-2">
        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Template name (as approved in Interakt)</Label><Input value={cur.name} onChange={(e) => setF({ ...cur, name: e.target.value })} className="h-9 font-mono text-sm" data-testid="ld-wa-template-name" /></div>
        <div className="space-y-1"><Label className="text-[11px] text-slate-500">Language</Label><Input value={cur.language} onChange={(e) => setF({ ...cur, language: e.target.value })} className="h-9 font-mono text-sm" data-testid="ld-wa-template-lang" /></div>
      </div>
      <div className="space-y-1"><Label className="text-[11px] text-slate-500">Body (must match the approved template word for word — used for the preview)</Label><Textarea value={cur.body} onChange={(e) => setF({ ...cur, body: e.target.value })} rows={5} className="font-mono text-xs" data-testid="ld-wa-template-body" /></div>
      <Button onClick={save} disabled={!f} className="gap-1.5 bg-slate-900 hover:bg-slate-800 mt-2" data-testid="ld-wa-template-save"><Save className="h-4 w-4" /> Save WhatsApp template</Button>
    </Card>
  );
}

export default function CatalogueLibrary() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["ld-catalogues-all"], queryFn: async () => (await api.get("/lead-desk/catalogues", { params: { include_inactive: 1 } })).data.catalogues });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ld-catalogues-all"] }); qc.invalidateQueries({ queryKey: ["ld-catalogues"] }); };
  const all = data || [];
  const withFile = all.filter((c) => c.has_file).length;
  return (
    <div data-testid="catalogue-library-page">
      <PageHeader title="Catalogue Library" subtitle={`PDF attachments used by the Lead Desk. ${withFile} of ${all.length} catalogues have a PDF uploaded.`} />
      {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-5 items-start">
          <div className="space-y-5">
            {BRANDS.map(([id, label]) => (
              <Card key={id} className="border-slate-200 overflow-hidden" data-testid={`cat-brand-${id}`}>
                <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center justify-between"><h3 className="font-heading font-semibold text-slate-900">{label}</h3><span className="text-xs text-slate-400">{all.filter((c) => c.brand === id).length} catalogues</span></div>
                {all.filter((c) => c.brand === id).map((c) => <Row key={c.id} c={c} onChanged={refresh} />)}
                <AddRow brand={id} onAdded={refresh} />
              </Card>
            ))}
          </div>
          <TemplateEditor />
          <WaTemplateEditor />
        </div>
      )}
    </div>
  );
}
